import { ClientEndpointType, EventTrigger, EventTriggerFacility, EventTriggers, Facilities, MaxServerIntValue } from "@app/shared";
import { useMemo, useState } from "react";
import { DropDownMenu, IconLink, Icons, PageContainer, SortableTable } from "@app/dashboard/src/components/shared";
import { CheckboxInput } from "@app/dashboard/src/components/shared/inputs";
import { useConfirmDialog, useEditModelDialog, useFetch } from "@app/shared/src/hooks";
import { EventTriggerEditor, OfflineTriggerDevice } from "./EditEventTrigger/EventTriggerEditor";

type EventTriggerTableProps = {
    organizationId: string;
    facilityId?: string;
    refreshKey?: number;
    onDataChange?: () => void;
    offlineMode?: boolean;
    offlineDevice?: OfflineTriggerDevice;
};

export const EventTriggerTable: React.FC<EventTriggerTableProps> = (props) => {
    const [eventTriggers, , fetchHelpers] = useFetch(() => EventTriggers.getByFacilityId(props.organizationId, props.facilityId), [props.organizationId, props.facilityId, props.refreshKey]);
    const [laneLineOptions] = useFetch(() => props.facilityId ? Facilities.getLaneLineOptions(props.organizationId, props.facilityId) : Promise.resolve(undefined), [props.organizationId, props.facilityId]);
    const laneLineNameById = useMemo(() => {
        const map = new Map<string, string>();
        laneLineOptions?.forEach((g) => g.options?.forEach((o) => o.value && map.set(o.value, o.label ?? "")));
        return map;
    }, [laneLineOptions]);

    // filter out triggers that have Luxcom endpoints, those are handled by LuxcomBiuSettings
    const filteredTriggers = useMemo(() => {
        if (!eventTriggers) return undefined;
        return eventTriggers.filter((trigger) => {
            const hasLuxcomEndpoint = trigger.eventTriggerClientEndpoints?.some((etc) => {
                if (etc.clientEndpoint?.endpointType === ClientEndpointType.Luxcom) return true;
                // fallback: check endpointConfig for Luxcom structure (firmware where clientEndpoint may not be populated)
                if (etc.endpointConfig) {
                    try { return JSON.parse(etc.endpointConfig).biuNum != null; } catch { /* not Luxcom config */ }
                }
                return false;
            });
            return !hasLuxcomEndpoint;
        });
    }, [eventTriggers]);
    const triggerDialog = useEditModelDialog(EventTrigger);
    const confirmDialog = useConfirmDialog();

    // Trigger ids with an enable/disable save in flight, so a slow request can't be double-toggled.
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

    /**
     * Flip a trigger on or off from the table. A trigger with no explicit IsEnabled predates the
     * field and is running, so the checkbox reads `!== false` and writes an explicit boolean.
     */
    const handleToggleEnabled = async (trigger: EventTrigger, enabled: boolean) => {
        if (!trigger.id || togglingIds.has(trigger.id)) return;
        setTogglingIds((prev) => new Set(prev).add(trigger.id!));
        try {
            trigger.isEnabled = enabled;
            await EventTriggers.update(trigger);
            props.onDataChange?.();
        } finally {
            // Refresh either way: on success it confirms, on failure it snaps the checkbox back to
            // what the server actually holds rather than leaving a lie on screen.
            await fetchHelpers.refreshData();
            setTogglingIds((prev) => {
                const next = new Set(prev);
                next.delete(trigger.id!);
                return next;
            });
        }
    };

    const handleAddTriggerOnClick = () => {
        triggerDialog.show(
            async (toSave: Partial<EventTrigger>) => {
                await EventTriggers.update(toSave as EventTrigger);
                await fetchHelpers.refreshData();
                props.onDataChange?.();
            },
            "Add Event Trigger",
            {
                $type: "",
                organizationId: props.organizationId,
                facilityId: props.facilityId,
                eventTriggerFacilities: props.facilityId ? [{ facilityId: props.facilityId } as EventTriggerFacility] : [],
                targetFilter: MaxServerIntValue,
                // Default to on-device processing. The firmware implements every trigger type; the server
                // only supports a subset in the cloud (see cloudProcessing.isCloudProcessingSupported), and
                // the toggle to opt into cloud is only shown for those types.
                processOnDevice: true,
            } as Partial<EventTrigger>,
        );
    };

    const handleEditTriggerOnClick = (toEdit: EventTrigger | undefined) => {
        if (!toEdit) return;

        triggerDialog.show(
            async (toSave: Partial<EventTrigger>) => {
                await EventTriggers.update(toSave as EventTrigger);
                await fetchHelpers.refreshData();
                props.onDataChange?.();
            },
            "Edit Event Trigger",
            toEdit,
        );
    };

    const handleDeleteTriggerOnClick = (toDelete: EventTrigger | undefined) => {
        if (!toDelete) return;
        confirmDialog.show("Delete Event?", "Are you sure you want to delete this event trigger?", "Delete", async () => {
            await EventTriggers.deleteTrigger(toDelete.id);
            await fetchHelpers.refreshData();
            props.onDataChange?.();
        });
    };

    return (
        <>
            <PageContainer
                Icon={Icons.Warning}
                title="Event Triggers"
                titleRight={
                    <IconLink Icon={Icons.PlusCircle} onClick={handleAddTriggerOnClick}>
                        Add New
                    </IconLink>
                }
            >
                <SortableTable
                    isLoading={fetchHelpers.isLoading}
                    emptyValue={<p className="m-5">No Event Triggers Found</p>}
                    columns={[
                        {
                            header: "Trigger",
                            width: 150,
                            filterable: true,
                            filterType: "text",
                            dataKey: "name",
                        },
                        {
                            header: "Enabled",
                            width: 70,
                            renderFunc: (e) =>
                                e && (
                                    <span title={e.isEnabled !== false ? "Enabled — click to disable" : "Disabled — click to enable"}>
                                        <CheckboxInput
                                            aria-label={`${e.name ?? "Trigger"} enabled`}
                                            checked={e.isEnabled !== false}
                                            disabled={!e.id || togglingIds.has(e.id)}
                                            onChange={(ev) => void handleToggleEnabled(e, ev.target.checked)}
                                        />
                                    </span>
                                ),
                        },
                        ...(!props.offlineMode
                            ? [
                                  {
                                      header: "Facilities",
                                      width: 150,
                                      renderFunc: (e: EventTrigger | undefined) => (!!!e?.facilities?.length ? "Any" : e.facilities.map((f) => f.name).join(", ")),
                                  },
                              ]
                            : []),
                        {
                            header: "SL / DZ / LL Filter",
                            width: 150,
                            renderFunc: (e) => {
                                const slDz = ((e?.screenLine?.name ?? "") + " " + (e?.detectionZone?.name ?? "")).trim();
                                const laneLines = (e?.laneLineIds ?? []).map((id) => laneLineNameById.get(id) ?? id).join(", ");
                                const parts = [slDz, laneLines].filter(Boolean);
                                return parts.length ? parts.join(" / ") : "Any";
                            },
                        },
                        ...(!props.offlineMode
                            ? [
                                  {
                                      header: "Devices",
                                      width: 125,
                                      renderFunc: (e: EventTrigger | undefined) => (!!!e?.devices?.length ? "Any" : e.devices.map((f) => f.name).join(", ")),
                                  },
                              ]
                            : []),
                        {
                            header: "Endpoints",
                            width: 150,
                            renderFunc: (e) => {
                                const endpointNames = e?.eventTriggerClientEndpoints?.map((etc) => etc?.clientEndpoint?.endpointName).filter(Boolean) || [];
                                return endpointNames.length > 0 ? endpointNames.join(", ") : "None";
                            },
                        },
                        ...(!props.offlineMode
                            ? [
                                  {
                                      // Where the trigger's condition is actually tested. Worth surfacing because
                                      // the two differ in ways an operator feels: an on-device trigger keeps firing
                                      // through a network outage, while a cloud one only fires when the device
                                      // manages to upload. Hidden in offlineMode — webconfig lists the device's own
                                      // triggers, which are on-device by definition, so the column would be a
                                      // constant.
                                      header: "Evaluated",
                                      width: 100,
                                      dataKey: "processOnDevice" as const,
                                      renderFunc: (e: EventTrigger | undefined) => {
                                          // Only an explicit false means cloud. A trigger that somehow arrives
                                          // without the field reads as on-device, matching how the server treats it.
                                          const onDevice = e?.processOnDevice !== false;
                                          return (
                                              <span
                                                  className="inline-flex items-center gap-1 whitespace-nowrap"
                                                  title={
                                                      onDevice
                                                          ? "Evaluated on the radar. Keeps firing while the device is offline, and is the only option for trigger types CoreApi has no detector for."
                                                          : "Evaluated by CoreApi as the device uploads targets. Nothing fires while the device cannot reach the cloud."
                                                  }
                                              >
                                                  {onDevice ? <Icons.HardwareChipOutline size={16} className="text-brand-primary" /> : <Icons.Cloud size={16} className="text-brand-primary" />}
                                                  {onDevice ? "Device" : "Cloud"}
                                              </span>
                                          );
                                      },
                                  },
                              ]
                            : []),
                        {
                            header: "",
                            width: 25,
                            renderFunc: (e) => (
                                <DropDownMenu>
                                    {/* Unsupported triggers (a subtype this firmware build degraded to the base
                                        EventTrigger) have no editable shape we understand — offer delete only. */}
                                    {!(e as { isUnsupported?: boolean } | undefined)?.isUnsupported && (
                                        <IconLink onClick={() => handleEditTriggerOnClick(e)} Icon={Icons.Edit}>
                                            Edit
                                        </IconLink>
                                    )}
                                    <IconLink onClick={() => handleDeleteTriggerOnClick(e)} Icon={Icons.Trash}>
                                        Delete
                                    </IconLink>
                                </DropDownMenu>
                            ),
                        },
                    ]}
                    data={filteredTriggers}
                    pagination
                    defaultPageSize={10}
                    pageSizeOptions={[5, 10, 25, 50]}
                    filtering
                />
            </PageContainer>
            {confirmDialog.renderDialog()}
            {triggerDialog.renderDialog((model, helpers) => model && <EventTriggerEditor trigger={model as EventTrigger} onChange={(newModel) => newModel && helpers.replaceModel(newModel)} facilityId={props.facilityId} offlineMode={props.offlineMode} offlineDevice={props.offlineDevice} />)}
        </>
    );
};
